/**
 * Orquestrador central de carregamento de acompanhamentos.
 *
 * Arquitetura Refatorada sob Princípios SOLID:
 * - SRP: Atua estritamente como Façade e ponto de entrada, delegando a busca
 *   para as estratégias de perfil concretas.
 * - DIP: Depende de abstrações de estratégia (AcompanhamentoStrategyFactory)
 *   em vez de blocos acoplados de if-else e endpoints espalhados.
 * - LSP & OCP: Qualquer nova estratégia de perfil pode ser conectada sem alterar
 *   os componentes consumidores.
 * - 100% de compatibilidade retroativa com a assinatura pública histórica do sistema.
 */

import { jwtDecode } from "jwt-decode";
import {
  obterIdentidadeSessao,
  obterNivelAcessoPagina,
} from "../utils/session.js";
import { AcompanhamentoStrategyFactory } from "./strategies/AcompanhamentoStrategyFactory.js";
import { criarLogger } from "./acompanhamentoHelpers.js";

// Subserviços especializados reexportados para compatibilidade com os consumidores
import {
  resolverCpfEstudanteCompleto,
  resolverTipoProgramaEstudante,
  listarAutorizacoesDoEstudante,
} from "./estudanteAcompanhamentoService.js";

import {
  buscarStatusCompletoDaEmpresa,
  obterExcecoesDoItem,
  obterRequerSupexDoItem,
} from "./statusCompletoService.js";

const logCarregar = criarLogger("Carregar");

/**
 * Carrega acompanhamentos do usuário conforme a estratégia resolvida pelo perfil da sessão.
 *
 * @param {string} token
 * @param {number} [page=1] (mantido para compatibilidade de assinatura)
 * @param {number} [limit=50] (mantido para compatibilidade de assinatura)
 * @param {object} [contexto={}] { busca?: string, empresaId?: string|number }
 * @param {AbortSignal} [signal]
 * @returns {Promise<Array<object>>}
 */
export async function carregarAcompanhamentosDoUsuario(
  token,
  page = 1,
  limit = 50,
  contexto = {},
  signal,
) {
  void page;
  void limit;

  const nivel = obterNivelAcessoPagina();
  const identidade = obterIdentidadeSessao();

  let tokenPayload = {};
  try {
    if (token) tokenPayload = jwtDecode(token);
  } catch (_) {}

  logCarregar("Iniciando carregamento via Strategy Factory...", {
    nivel,
    tipoIdentidade: identidade?.tipo,
    tipoToken: tokenPayload?.tipo,
  });

  // Obtém a estratégia adequada (CIEE, Estudante, Empresa ou Consultor)
  const strategy = AcompanhamentoStrategyFactory.criar({
    identidade,
    tokenPayload,
    nivel,
  });

  const itens = await strategy.carregar({
    token,
    page,
    limit,
    contexto,
    identidade,
    nivel,
    signal,
  });

  logCarregar("Carregamento concluído com sucesso. Total de itens:", itens.length);
  return itens;
}

// Re-exportações públicas mantendo paridade absoluta com os consumidores existentes
export {
  resolverCpfEstudanteCompleto,
  resolverTipoProgramaEstudante,
  listarAutorizacoesDoEstudante,
  buscarStatusCompletoDaEmpresa,
  obterExcecoesDoItem,
  obterRequerSupexDoItem,
};
